import React, { useCallback } from 'react';

import dayjs from 'dayjs';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { CatalogRoom, StopsellRoom } from '~/types/stopsell';

import { pluralizeToString } from '~/utils/stringUtils';

function formatDateRange(start: string, end: string): string {
  const s = dayjs(start);
  const e = dayjs(end);
  if (s.isSame(e, 'day')) {
    return s.format('DD MMM');
  }
  if (s.month() === e.month()) {
    return `${s.format('DD')}–${e.format('DD MMM')}`;
  }
  return `${s.format('DD MMM')} – ${e.format('DD MMM')}`;
}

function countNights(start: string, end: string): number {
  return dayjs(end).diff(dayjs(start), 'day') + 1;
}

interface Props {
  room: StopsellRoom;
  selected: Array<boolean>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  onChange: (dateIndex: number, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
}

export default function StopsellRoomGroup({
  room,
  selected,
  catalogRooms,
  selectedRoomTypeId,
  onChange,
  onToggleAll,
  onRoomTypeChange,
}: Props) {
  const allSelected = selected.every(Boolean);
  const anySelected = selected.some(Boolean);
  const indeterminate = anySelected && !allSelected;

  const handleToggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleAll(!allSelected);
    },
    [allSelected, onToggleAll],
  );

  const isMapped = !!selectedRoomTypeId;

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'grey.50' }}
        component="div"
        role="button"
        tabIndex={0}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 1 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => {
              e.stopPropagation();
              onToggleAll(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <Typography fontWeight={500} variant="body2">
              {room.name}
            </Typography>
            {catalogRooms.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={selectedRoomTypeId}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onRoomTypeChange(e.target.value)}
                error={!isMapped}
                sx={{ fontSize: 12, maxWidth: 320 }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.disabled">
                    Select room type…
                  </Typography>
                </MenuItem>
                {catalogRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
          <Chip label={pluralizeToString('date range', room.dates.length)} size="small" sx={{ bgcolor: 'grey.200' }} />
          <Box>
            <Button size="small" onClick={handleToggleAll}>
              {allSelected ? 'Deselect all' : 'Select all dates'}
            </Button>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack divider={<Divider />}>
          {room.dates.map((date, dateIndex) => (
            <Stack key={dateIndex} direction="row" alignItems="center" gap={1} sx={{ pl: 5, pr: 2, py: 0.75 }}>
              <Checkbox
                checked={selected[dateIndex]}
                onChange={(e) => onChange(dateIndex, e.target.checked)}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {formatDateRange(date.start, date.end)}
              </Typography>
              <Chip
                label={`${countNights(date.start, date.end)} nights`}
                size="small"
                sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: 11 }}
              />
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
